import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Panel } from './Panel';

/**
 * Placeholders shaped like the content they stand in for.
 *
 * A centred spinner tells you to wait and nothing else: the screen is empty,
 * then a moment later it is full, and everything below the fold jumps. These
 * reserve the real geometry — the same tile heights, the same row heights, the
 * same number of columns — so data arriving swaps grey for text and nothing
 * moves. That is what stops the flicker; the shimmer is just manners.
 *
 * Each skeleton mirrors a specific screen. When one of those layouts changes,
 * its skeleton has to change with it, or the jump comes back.
 */

/** One placeholder block. Size and shape come from `className`. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cn('skeleton block rounded-control', className)} />;
}

/**
 * Announces the wait once, for screen readers, and hides the blocks from them.
 *
 * Without this a reader would try to narrate several dozen empty boxes.
 */
export function SkeletonRegion({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** Lines of body text. The last is short, the way a real paragraph ends. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-3', i === lines - 1 ? 'w-2/5' : 'w-full')} />
      ))}
    </div>
  );
}

/**
 * The stat row that opens most screens.
 *
 * `min-h-22` matches StatTile exactly, so the row does not change height when
 * the numbers land.
 */
export function SkeletonStatTiles({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3', className)}>
      {Array.from({ length: count }, (_, i) => (
        <Panel key={i} className="flex min-h-22 flex-col justify-center gap-2 px-4 py-3.5">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-6 w-20" />
        </Panel>
      ))}
    </div>
  );
}

/** Rows in a list: badge, two lines of text, a trailing control. */
export function SkeletonRows({ count = 6, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-control border border-border bg-surface-raised px-3 py-2.5"
        >
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3 w-2/5" />
            <Skeleton className="h-2.5 w-1/4" />
          </div>
          <Skeleton className="h-6 w-16 shrink-0 rounded-pill" />
        </div>
      ))}
    </div>
  );
}

/** The contest grid: title, two figures, a fill bar and a call to action. */
export function SkeletonCards({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3', className)}>
      {Array.from({ length: count }, (_, i) => (
        <Panel key={i} className="flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Skeleton className="h-2.5 w-20" />
              <Skeleton className="h-5 w-3/4" />
            </div>
            <Skeleton className="h-5 w-14 shrink-0 rounded-pill" />
          </div>

          <div className="my-5 grid grid-cols-2 gap-3">
            {[0, 1].map((n) => (
              <div key={n} className="flex flex-col gap-2">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </div>

          <Skeleton className="mb-4 h-1.5 w-full rounded-pill" />
          <Skeleton className="mt-auto h-10 w-full" />
        </Panel>
      ))}
    </div>
  );
}

/** A panel of stacked content: heading, sub-line, then body. */
export function SkeletonPanel({
  lines = 4,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <Panel className={className}>
      <div className="mb-4 flex flex-col gap-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-2.5 w-56" />
      </div>
      <SkeletonText lines={lines} />
    </Panel>
  );
}

/**
 * The route-level fallback, shown while a screen's own code is still loading.
 *
 * Deliberately generic — at this point the router knows which screen is coming
 * but the component that describes its shape has not been fetched yet — so it
 * commits only to the parts every screen shares: a title block and some
 * panels.
 */
export function SkeletonPage({ label = 'Loading' }: { label?: string }) {
  return (
    <SkeletonRegion label={label}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-7 w-52" />
          <Skeleton className="h-2.5 w-36" />
        </div>
        <Skeleton className="hidden size-9 shrink-0 rounded-full lg:block" />
      </div>

      <SkeletonStatTiles className="mb-4" />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SkeletonPanel />
        <SkeletonPanel />
      </div>
    </SkeletonRegion>
  );
}
